import React from "react";
import { gql, useQuery } from "@apollo/client";
import { Pressable, View, Platform } from "react-native";
import { nativeApplicationVersion, nativeBuildVersion } from "expo-application";
import { ReactNode, useState } from "react";
import { Feather } from "@expo/vector-icons";
import { Badge, Text } from "react-native-paper";

import { pgaLink, PLAN_LINK, JOB_PREFERENCES_LINK, CAREER_SERVICES_LINK } from "../links";
import useCurrentUser, { NotificationPermission } from "../../hooks/useCurrentUser";
import { CURRENT_MEMBER_FRAGMENT } from "../fragments";
import { Anchor, AvatarProfile, ScreenContainer, ScrollableContainer } from "../../components";
import PgaSquareIcon from "../../svgs/PgaSquareIcon";
import AccountIcon from "../../svgs/AccountIcon";
import PreferencesIcon from "../../svgs/PreferencesIcon";
import ViewOpenIcon from "../../svgs/ViewOpenIcon";
import AccountsIcon from "../../svgs/AccountsIcon";
import GolfRetirementIcon from "../../svgs/GolfRetirementIcon";
import GlobeIcon from "../../svgs/GlobeIcon";
import { useIntercomClient } from "../../hooks/useIntercom";
import HelpIcon from "../../svgs/HelpIcon";
import config from "../../config";
import { MemberIdCardModal } from "../../components/MemberIdCardModal";
import IdCardIcon from "../../svgs/IdCardIcon";
import { colors } from "../../../theme";
import { ScreenProps } from "./types";
import { useSettingsBadgeCount } from "./hooks";
import TabJobsIcon from "../../svgs/TabJobsIcon";
import TabBenefitsIcon from "../../svgs/TabBenefitsIcon";
import { useFeatureFlags } from "../../hooks/useFlagr";
import { renderName } from "./utils";

export const QUERY = gql`
  query getCurrentMember {
    currentMember {
      ...CurrentMemberFragment
      primaryEmail
      profilePhoto
    }
  }
  ${CURRENT_MEMBER_FRAGMENT}
`;

interface CurrentMember {
  primaryEmail: string;
  profilePhoto?: string;
  displayName: string;
  informalName: string;
}

interface Query {
  currentMember: CurrentMember;
}

const appVersion = [nativeApplicationVersion, nativeBuildVersion, config.ciBuildNumber].filter((v) => !!v).join("-");

const Item = ({ name, icon, children }: { name: string; icon: ReactNode; children?: ReactNode }) => {
  return (
    <View className="flex-row items-center py-4 px-6">
      <View className="w-6 h-6 items-center justify-center">{icon}</View>
      <Text className="ml-3 text-regular-tight font-400">{name}</Text>
      {children}
    </View>
  );
};

const ItemLink = ({ href, name, icon }: { href: string; name: string; icon: ReactNode }) => {
  return (
    <Anchor href={href} aClassName="active:bg-sky-light">
      <Item name={name} icon={icon} />
    </Anchor>
  );
};

const ItemPressable = ({
  onPress,
  name,
  icon,
  testID,
  children,
}: {
  onPress: () => void;
  name: string;
  icon: ReactNode;
  testID?: string;
  children?: ReactNode;
}) => {
  return (
    <Pressable className="active:bg-sky-light" {...{ onPress, testID }}>
      <Item name={name} icon={icon}>
        {children}
      </Item>
    </Pressable>
  );
};

const MemberIdCard = () => {
  const [memberIdCardModalVisible, setMemberIdCardModalVisible] = useState(false);

  return (
    <>
      <MemberIdCardModal
        {...{
          visible: memberIdCardModalVisible,
          setVisible: setMemberIdCardModalVisible,
        }}
      />
      <ItemPressable
        name="Member ID"
        onPress={() => setMemberIdCardModalVisible(true)}
        icon={<IdCardIcon color={colors.ink.darkest} />}
        testID="member-id-pressable"
      />
    </>
  );
};

const Divider = () => <View className="border-b border-sky-light mx-6" />;

function SettingsItem({
  onPress,
  acknowledgedNotificationBadgeState,
}: {
  onPress(): void;
  acknowledgedNotificationBadgeState: NotificationPermission;
}) {
  const settingsBadgeCount = useSettingsBadgeCount(acknowledgedNotificationBadgeState);

  return (
    <ItemPressable name="Settings" onPress={onPress} icon={<Feather size={22} name="settings" />}>
      <View className="flex-1">
        <Badge testID="settings-badge" className="bg-red" visible={settingsBadgeCount > 0} size={32}>
          {settingsBadgeCount}
        </Badge>
      </View>
    </ItemPressable>
  );
}

export default function AccountScreen({ navigation }: ScreenProps<"AccountShow">) {
  const { logout, currentUser, acknowledgedNotificationBadgeState, canImpersonate, impersonatedUser } = useCurrentUser();
  const { firstName, lastName } = currentUser!;
  const flags = useFeatureFlags();
  const { data } = useQuery<Query>(QUERY);
  const intercom = useIntercomClient();

  const navigateToJobPreferences = () => navigation.navigate("JobPreferences");
  const navigateToJobs = () => navigation.navigate("Jobs");
  const navigateToAccountEdit = () => navigation.navigate("AccountEdit");
  const navigateToInvoiceList = () => navigation.navigate("InvoiceList", { slug: "invoices" });

  const { displayName, informalName } = data?.currentMember || {};
  const name = renderName(displayName, informalName);

  return (
    <ScreenContainer>
      <ScrollableContainer containerClassName="bg-white" testID="account-screen">
        <View className="px-6 pt-10">
          <Text className="font-700 text-title-2 text-primary-dark">Account</Text>

          <View className="flex-row mt-9 mb-2 items-center">
            <AvatarProfile
              {...{
                profilePhoto: data?.currentMember.profilePhoto,
                firstName,
                lastName,
              }}
            />
            <View className="ml-4 items-start flex-1">
              <Text className="text-title-4 font-700">{name}</Text>
              <Text className="text-regular-normal font-400 mt-1">{data?.currentMember.primaryEmail}</Text>
            </View>
          </View>
        </View>
        <SettingsItem
          {...{ acknowledgedNotificationBadgeState }}
          onPress={() => {
            navigation.navigate("AccountSettings");
          }}
        />
        {!impersonatedUser && (
          <>
            <Divider />
            <ItemPressable name="Logout" onPress={logout} icon={<Feather size={22} name="log-out" />} />
          </>
        )}

        <View className="bg-sky-lightest h-3 mt-4" />

        <Text className="text-large-normal font-700 mt-6 mb-2 px-6">My Membership</Text>
        {Platform.OS !== "web" && <MemberIdCard />}

        <Divider />
        <ItemPressable name="My PGA Account" onPress={navigateToAccountEdit} icon={<PgaSquareIcon />} />

        <Divider />

        {flags["membership-unified-invoices"] && (
          <ItemPressable
            name="Billing & Payments"
            onPress={navigateToInvoiceList}
            icon={<Feather size={22} name="credit-card" />}
          />
        )}

        <ItemLink name="My Directory Profile" href={pgaLink("https://account.pga.org/profile/edit")} icon={<AccountIcon />} />

        <Text className="text-large-normal font-700 mt-10 mb-2 px-6">Jobs</Text>

        {Platform.OS === "web" ? (
          <ItemLink name="View Open Jobs on PGA.org" href={pgaLink("https://jobs.pga.org/")} icon={<ViewOpenIcon />} />
        ) : (
          <ItemPressable name="Search Jobs" onPress={navigateToJobs} icon={<TabJobsIcon color={colors.ink.darkest} />} />
        )}

        <Divider />

        {Platform.OS === "web" ? (
          <ItemLink name="Job Preferences" href={pgaLink(JOB_PREFERENCES_LINK)} icon={<PreferencesIcon />} />
        ) : (
          <ItemPressable name="Job Preferences" onPress={navigateToJobPreferences} icon={<PreferencesIcon />} />
        )}

        <Divider />

        <ItemLink name="Career Services" href={CAREER_SERVICES_LINK} icon={<AccountsIcon />} />
        <Text className="text-large-normal font-700 mt-10 mb-2 px-6">Help & Resources</Text>

        <ItemPressable name="Get Help" onPress={() => intercom.present()} icon={<HelpIcon />} />

        <Divider />

        <ItemLink
          name="Deferred Compensation Plan Info."
          href={PLAN_LINK}
          icon={<TabBenefitsIcon color={colors.ink.darkest} />}
        />

        <Divider />

        <ItemLink
          name="Golf Retirement Plus"
          href="https://resources.pga.org/member-benefits/golf-retirement-plus"
          icon={<GolfRetirementIcon />}
        />

        <Divider />

        <ItemLink name="PGA.org" href={pgaLink("https://account.pga.org/")} icon={<GlobeIcon />} />

        {canImpersonate && !impersonatedUser && (
          <>
            <Text className="text-large-normal font-700 mt-10 mb-2 px-6">Admin</Text>
            <ItemPressable
              name="Assume User"
              onPress={() => navigation.navigate("AssumeUser")}
              icon={<Feather size={22} name="users" />}
            />
          </>
        )}

        {!!appVersion && (
          <Text className="self-center mt-12 mb-6 text-small-tight text-ink-lighter font-400">App Version {appVersion}</Text>
        )}
      </ScrollableContainer>
    </ScreenContainer>
  );
}
